/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { usePmsService } from "../hooks/useAdminServices";
import { useHotel } from "./HotelContext";
import { logger } from "../utils/logger";
import { PmsStatusDto } from "../services/admin/types/pms.types";

interface PmsConfigurationContextType {
  /** Whether PMS is fully configured (connected AND property linked) */
  isPmsConfigured: boolean;
  /** Whether the initial PMS status check is loading */
  isLoading: boolean;
  /** Full PMS status for detailed checks */
  pmsStatus: PmsStatusDto | null;
  /** Manually refresh PMS status (e.g., after completing PMS setup) */
  refreshPmsStatus: () => Promise<void>;
}

const PmsConfigurationContext = createContext<
  PmsConfigurationContextType | undefined
>(undefined);

export const PmsConfigurationProvider: React.FC<{
  children: React.ReactNode;
  initialPmsStatus?: PmsStatusDto;
}> = ({ children, initialPmsStatus }) => {
  const { isAuthenticated } = useAuth0();
  const { myHotelInfo } = useHotel();
  const [pmsStatus, setPmsStatus] = useState<PmsStatusDto | null>(initialPmsStatus || null);
  const [isLoading, setIsLoading] = useState(!initialPmsStatus);
  const [hasInitialized, setHasInitialized] = useState(!!initialPmsStatus);
  const pmsService = usePmsService();
  const isUpdatingRef = useRef(false);

  // Check if user has a hotel before making API calls
  const hasHotel = !!myHotelInfo?.hotelId;

  // Determine if PMS is fully configured
  const isPmsConfigured = !!(
    pmsStatus?.isConnected && pmsStatus?.linkedProperty
  );

  const fetchPmsStatus = useCallback(async () => {
    // Only fetch if user has a hotel - avoids errors
    if (!isAuthenticated || !pmsService || !hasHotel || isUpdatingRef.current) {
      return;
    }

    isUpdatingRef.current = true;
    try {
      const result = await pmsService.getPmsStatus();
      if (result.success) {
        setPmsStatus(result.data);
      } else {
        // Not an error - hotel just doesn't have PMS configured
        // Set default unconfigured state
        setPmsStatus({
          isConnected: false,
          grantedScopes: [],
          requiredScopes: [],
          missingScopes: [],
          linkedProperty: null,
        });
      }
    } catch (error) {
      logger.error("Failed to fetch PMS status:", error);
      // Set default unconfigured state on error
      setPmsStatus({
        isConnected: false,
        grantedScopes: [],
        requiredScopes: [],
        missingScopes: [],
        linkedProperty: null,
      });
    } finally {
      setIsLoading(false);
      isUpdatingRef.current = false;
    }
  }, [isAuthenticated, pmsService, hasHotel]);

  const refreshPmsStatus = useCallback(async () => {
    setIsLoading(true);
    return fetchPmsStatus();
  }, [fetchPmsStatus]);

  // Initialize once when service becomes available AND user has a hotel
  useEffect(() => {
    if (isAuthenticated && pmsService && hasHotel && !hasInitialized) {
      setHasInitialized(true);
      fetchPmsStatus();
    }
  }, [isAuthenticated, pmsService, hasHotel, hasInitialized, fetchPmsStatus]);

  // If user doesn't have a hotel yet, mark as not loading with unconfigured state
  useEffect(() => {
    if (isAuthenticated && !hasHotel && isLoading) {
      setIsLoading(false);
      setPmsStatus(null);
    }
  }, [isAuthenticated, hasHotel, isLoading]);

  return (
    <PmsConfigurationContext.Provider
      value={{
        isPmsConfigured,
        isLoading,
        pmsStatus,
        refreshPmsStatus,
      }}
    >
      {children}
    </PmsConfigurationContext.Provider>
  );
};

export const usePmsConfiguration = (): PmsConfigurationContextType => {
  const context = useContext(PmsConfigurationContext);
  if (!context) {
    throw new Error(
      "usePmsConfiguration must be used within PmsConfigurationProvider",
    );
  }
  return context;
};
